import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Grid, Card, CardContent,
  TextField, LinearProgress, Alert, Avatar, Tooltip, MenuItem,
  Select, FormControl, InputLabel, IconButton,
} from '@mui/material';
import { CalendarMonth, ChevronLeft, ChevronRight, Home, BeachAccess, Fingerprint, MeetingRoom as MeetingRoomIcon, Work } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const AVATAR_COLORS = ['#1a237e','#b71c1c','#1b5e20','#e65100','#4a148c','#006064','#880e4f'];
function avatarColor(name: string) { return AVATAR_COLORS[(name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % AVATAR_COLORS.length]; }
function initials(first?: string, last?: string) { return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase(); }

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function getWeekDates(monday: Date): Date[] {
  return WEEKDAYS.map((_, i) => { const d = new Date(monday); d.setDate(d.getDate() + i); return d; });
}
function getMondayOfWeek(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const m = new Date(d);
  m.setDate(diff);
  m.setHours(0, 0, 0, 0);
  return m;
}
function isoDate(d: Date) { return d.toISOString().slice(0, 10); }
function fmtMonthYear(d: Date) { return d.toLocaleDateString('en-HK', { month: 'long', year: 'numeric' }); }

// Day status types — priority order
type DayStatus = 'holiday' | 'leave' | 'wfh' | 'present' | 'absent' | 'weekend' | 'future' | 'none';

const STATUS_DISPLAY: Record<DayStatus, { bg: string; text: string; icon: React.ReactNode; label: string }> = {
  holiday: { bg: '#fff3e0', text: '#e65100', icon: <BeachAccess sx={{ fontSize: 12 }} />, label: 'Holiday' },
  leave:   { bg: '#e3f2fd', text: '#1565c0', icon: <BeachAccess sx={{ fontSize: 12 }} />, label: 'Leave' },
  wfh:     { bg: '#f3e5f5', text: '#6a1b9a', icon: <Home sx={{ fontSize: 12 }} />, label: 'WFH' },
  present: { bg: '#e8f5e9', text: '#2e7d32', icon: <Fingerprint sx={{ fontSize: 12 }} />, label: 'In Office' },
  absent:  { bg: '#fce4ec', text: '#c62828', icon: <Work sx={{ fontSize: 12 }} />, label: 'Absent' },
  weekend: { bg: '#fafafa', text: '#9e9e9e', icon: null, label: '' },
  future:  { bg: '#fafafa', text: '#bdbdbd', icon: null, label: '' },
  none:    { bg: '#fafafa', text: '#bdbdbd', icon: null, label: '?' },
};

interface Employee { id: string; employeeId: string; firstName: string; lastName: string; department?: string; jobTitle?: string; }
interface LeaveRecord { employeeId: string; startDate: string; endDate: string; status: string; }
interface WfhRecord { employeeId: string; date: string; status: string; }
interface AttRecord { employeeId: string; date: string; status: string; clockIn?: string; }

function DayCell({ status, tooltip }: { status: DayStatus; tooltip?: string }) {
  const cfg = STATUS_DISPLAY[status];
  const cell = (
    <Box sx={{
      width: '100%', height: 28, bgcolor: cfg.bg, borderRadius: 0.5,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: cfg.text, fontSize: '0.6rem', gap: 0.3,
    }}>
      {cfg.icon}
      {cfg.label && <span style={{ fontWeight: 600 }}>{cfg.label}</span>}
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip}>{cell}</Tooltip> : cell;
}

export default function Roster() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const [monday, setMonday] = useState<Date>(getMondayOfWeek(new Date()));
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [deptFilter, setDeptFilter] = useState('');
  const [departments, setDepartments] = useState<string[]>([]);
  const [leaves, setLeaves] = useState<LeaveRecord[]>([]);
  const [wfhs, setWfhs] = useState<WfhRecord[]>([]);
  const [attendance, setAttendance] = useState<AttRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const weekDates = getWeekDates(monday);
  const weekStart = isoDate(weekDates[0]);
  const weekEnd   = isoDate(weekDates[4]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [empData, leaveData, wfhData, attData] = await Promise.all([
        apiFetch('/api/employees?limit=500&status=active'),
        apiFetch(`/api/leave/requests/calendar?startDate=${weekStart}&endDate=${weekEnd}`).catch(() => []),
        apiFetch(`/api/wfh?from=${weekStart}&to=${weekEnd}&status=approved`).catch(() => []),
        apiFetch(`/api/attendance/date?date=${weekStart}`).catch(() => []), // single day for now
      ]);

      const emps: Employee[] = empData.data || empData || [];
      setEmployees(emps);
      const depts = Array.from(new Set(emps.map((e: Employee) => e.department).filter(Boolean))) as string[];
      setDepartments(depts.sort());
      setLeaves(Array.isArray(leaveData) ? leaveData : leaveData.data || []);
      setWfhs(Array.isArray(wfhData) ? wfhData : wfhData.data || []);
      setAttendance(Array.isArray(attData) ? attData : attData.logs || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [weekStart, weekEnd]);

  useEffect(() => { load(); }, [load]);

  const prevWeek = () => { const d = new Date(monday); d.setDate(d.getDate() - 7); setMonday(d); };
  const nextWeek = () => { const d = new Date(monday); d.setDate(d.getDate() + 7); setMonday(d); };
  const thisWeek = () => setMonday(getMondayOfWeek(new Date()));

  const getStatus = (empId: string, date: Date): { status: DayStatus; detail?: string } => {
    const dateStr = isoDate(date);
    const today = isoDate(new Date());
    const isFuture = dateStr > today;

    // On leave?
    const leave = leaves.find(l => l.employeeId === empId && dateStr >= l.startDate && dateStr <= l.endDate);
    if (leave) return { status: 'leave', detail: 'On Leave' };

    // WFH approved?
    const wfh = wfhs.find(w => w.employeeId === empId && w.date === dateStr);
    if (wfh) return { status: 'wfh', detail: 'Working from Home' };

    // Attendance record?
    const att = attendance.find(a => a.employeeId === empId && a.date?.slice(0, 10) === dateStr);
    if (att) {
      if (att.status === 'present') return { status: 'present', detail: att.clockIn ? `In at ${att.clockIn.slice(11, 16)}` : 'Present' };
      if (att.status === 'absent') return { status: 'absent', detail: 'Absent' };
      if (att.status === 'work_from_home') return { status: 'wfh', detail: 'WFH' };
    }

    if (isFuture) return { status: 'future' };
    return { status: 'none' };
  };

  const filteredEmps = deptFilter ? employees.filter(e => e.department === deptFilter) : employees;

  // Summary: count statuses for each day
  const daySummary = weekDates.map(d => {
    const counts: Record<string, number> = {};
    for (const emp of filteredEmps) {
      const { status } = getStatus(emp.id, d);
      if (['present','wfh','leave','absent'].includes(status)) {
        counts[status] = (counts[status] ?? 0) + 1;
      }
    }
    return counts;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonth sx={{ fontSize: 32, color: '#1a237e' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Team Roster</Typography>
            <Typography variant="body2" color="text.secondary">Weekly view: attendance, WFH, and leave</Typography>
          </Box>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Department</InputLabel>
          <Select value={deptFilter} label="Department" onChange={e => setDeptFilter(e.target.value)}>
            <MenuItem value="">All Departments</MenuItem>
            {departments.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Week Navigator */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={prevWeek}><ChevronLeft /></IconButton>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1, textAlign: 'center' }}>
            {fmtMonthYear(monday)} · Week of {monday.toLocaleDateString('en-HK', { day: 'numeric', month: 'short' })}
          </Typography>
          <IconButton onClick={nextWeek}><ChevronRight /></IconButton>
          <Button size="small" variant="outlined" onClick={thisWeek}>Today</Button>
        </Box>

        {/* Day summary row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '180px repeat(5, 1fr)', gap: 1, mb: 1 }}>
          <Box />
          {weekDates.map((d, i) => {
            const isToday = isoDate(d) === isoDate(new Date());
            const summary = daySummary[i];
            return (
              <Box key={i} sx={{ textAlign: 'center' }}>
                <Typography variant="caption" fontWeight={isToday ? 900 : 600}
                  sx={{ color: isToday ? '#1a237e' : 'text.primary', display: 'block' }}>
                  {WEEKDAYS[i]}
                </Typography>
                <Typography variant="body2" fontWeight={isToday ? 900 : 400}
                  sx={{ color: isToday ? '#1a237e' : 'text.secondary',
                    bgcolor: isToday ? '#e8eaf6' : 'transparent',
                    borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto' }}>
                  {d.getDate()}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.3, mt: 0.5, flexWrap: 'wrap' }}>
                  {summary.present > 0 && <Chip label={`${summary.present} in`} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.55rem', height: 16 }} />}
                  {summary.wfh > 0 && <Chip label={`${summary.wfh} wfh`} size="small" sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontSize: '0.55rem', height: 16 }} />}
                  {summary.leave > 0 && <Chip label={`${summary.leave} leave`} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.55rem', height: 16 }} />}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Employee rows */}
      <Paper>
        {filteredEmps.map((emp, empIdx) => {
          const name = `${emp.firstName} ${emp.lastName}`;
          return (
            <Box key={emp.id} sx={{
              display: 'grid', gridTemplateColumns: '180px repeat(5, 1fr)',
              gap: 1, p: 1, borderTop: empIdx > 0 ? '1px solid #f0f0f0' : 'none',
              alignItems: 'center',
              '&:hover': { bgcolor: '#f9f9f9' },
            }}>
              {/* Name cell */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                <Avatar sx={{ width: 30, height: 30, bgcolor: avatarColor(name), fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                  {initials(emp.firstName, emp.lastName)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{emp.firstName} {emp.lastName}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>{emp.department}</Typography>
                </Box>
              </Box>

              {/* Day cells */}
              {weekDates.map((d, di) => {
                const { status, detail } = getStatus(emp.id, d);
                return (
                  <Box key={di}>
                    <DayCell status={status} tooltip={detail} />
                  </Box>
                );
              })}
            </Box>
          );
        })}
        {filteredEmps.length === 0 && !loading && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>No employees to display.</Typography>
          </Box>
        )}
      </Paper>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
        {(['present','wfh','leave','absent','future'] as DayStatus[]).map(s => (
          <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: STATUS_DISPLAY[s].bg, border: `1px solid ${STATUS_DISPLAY[s].text}20`, borderRadius: 0.5 }} />
            <Typography variant="caption" color="text.secondary">{STATUS_DISPLAY[s].label || s}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
